import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { apiResource, b2chatBaseQuery } from '@src/client';
import B2ChatClient from '@src/client';
import type {
  ResetUserMfaRequest,
  VerifyRecoveryCodeRequest,
  VerifyRecoveryCodeResponse,
  VerifyTotpChallengeRequest,
  VerifyTotpChallengeResponse,
} from '@src/components/Login/TotpChallenge/types';
import type {
  EnrollmentResponse,
  RolloutNoticeResponse,
  VerifyEnrollmentRequest,
  VerifyEnrollmentResponse,
} from '@src/components/Login/TotpEnrollment/types';

const mfaApi = createApi({
  reducerPath: 'mfaApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['mfa-enrollment'],
  endpoints: builder => ({
    /**
     * Ask for a setup key ONCE per enrollment, and hold what comes back.
     *
     * Each call mints a fresh TOTP registration at the provider and invalidates the key before it,
     * and the provider locks the account for an hour once there are too many — on 2026-09-16 that
     * cost twenty users 602 refused calls.
     *
     * Callers must therefore pass {@link ENROLLMENT_CACHE_KEY} as `fixedCacheKey`, so going back a
     * step and returning shows the SAME key, and call `reset()` once the enrollment ends.
     */
    startEnrollment: builder.mutation<EnrollmentResponse, void>({
      query: () =>
        apiResource(B2ChatClient.resources.mfa.actions.startEnrollment, {
          withJWT: true,
        }),
    }),

    verifyEnrollment: builder.mutation<
      VerifyEnrollmentResponse,
      VerifyEnrollmentRequest
    >({
      query: ({ code }) =>
        apiResource(B2ChatClient.resources.mfa.actions.verifyEnrollment, {
          data: { code },
          withJWT: true,
        }),
    }),

    ackRecoveryCodes: builder.mutation<void, void>({
      query: () =>
        apiResource(B2ChatClient.resources.mfa.actions.ackRecoveryCodes, {
          withJWT: true,
        }),
      invalidatesTags: ['mfa-enrollment'],
    }),

    verifyTotpChallenge: builder.mutation<
      VerifyTotpChallengeResponse,
      VerifyTotpChallengeRequest
    >({
      query: ({ code, rememberDevice }) =>
        apiResource(B2ChatClient.resources.mfa.actions.verifyTotpChallenge, {
          data: { code, rememberDevice },
          withJWT: true,
        }),
    }),

    verifyRecoveryCode: builder.mutation<
      VerifyRecoveryCodeResponse,
      VerifyRecoveryCodeRequest
    >({
      query: ({ recoveryCode }) =>
        apiResource(B2ChatClient.resources.mfa.actions.verifyRecoveryCode, {
          data: { recoveryCode },
          withJWT: true,
        }),
    }),

    resetUserMfa: builder.mutation<void, ResetUserMfaRequest>({
      query: ({ userId, closeSessions }) =>
        apiResource(B2ChatClient.resources.users.actions.resetUserMfa, {
          params: { userId },
          data: { closeActiveSessions: closeSessions ?? false },
          withJWT: true,
        }),
    }),

    getRolloutNotice: builder.query<RolloutNoticeResponse, void>({
      query: () =>
        apiResource(B2ChatClient.resources.mfa.actions.getRolloutNotice, {
          withJWT: true,
        }),
    }),
  }),
});

/**
 * The `fixedCacheKey` entries the MFA screens share, spelled once so two spellings cannot silently
 * become two entries.
 *
 * The enrollment key makes the setup key outlive the screen that asked for it. The other three make
 * a LOCK outlive its screen: held in component state it was erased by any remount, and each of
 * these screens can be remounted in two clicks (a recovery link, a Back link to setup, or browser
 * Back/Forward), which brought back a live Verify button and another attempt against the lock.
 * A login clears the whole API, which is the lifetime a lock should have here.
 */
export const ENROLLMENT_CACHE_KEY = 'mfa-totp-enrollment';
export const CHALLENGE_CACHE_KEY = 'mfa-totp-challenge';
export const CONFIRM_CACHE_KEY = 'mfa-totp-confirm';
export const RECOVERY_CACHE_KEY = 'mfa-totp-recovery';

/**
 * The failure codes this app branches on, spelled once.
 *
 * These are a contract with the backend, not labels we own: they come from `ProviderErrorResponses`
 * and the MFA exception handlers in botbackoffice. As literals spread across the screens, a
 * server-side rename would un-fix one and leave the rest working, with nothing failing to compile.
 */
export const MFA_ERROR_CODE = {
  /** The provider locked the account. Waiting is the only remedy, and it is measured in hours. */
  userLocked: 'MFA_USER_LOCKED',
  /** Our own cooldown between two enrollment starts. */
  enrollmentTooSoon: 'MFA_ENROLLMENT_TOO_SOON',
  /** This session has spent its budget of setup keys. */
  enrollmentRestartLimit: 'MFA_ENROLLMENT_RESTART_LIMIT',
  /** The user already has TOTP: the enrollment should not have been offered, so it goes to the
   * challenge instead of showing an error for something that is not wrong. */
  alreadyEnrolled: 'MFA_ALREADY_ENROLLED',
  /** The window between the password and the second factor closed. */
  windowExpired: 'MFA_WINDOW_EXPIRED',
  /** The user has a factor they have not proved in this session, so no setup key is handed out. */
  secondFactorRequired: 'MFA_SECOND_FACTOR_REQUIRED',
  /** The half-authenticated session backing the challenge is gone. Same remedy as the window
   * closing — sign in again — which is why the two are branched on together. */
  sessionInvalid: 'MFA_SESSION_INVALID',
} as const;

/**
 * The shape an MFA failure actually arrives in.
 *
 * `b2chatBaseQuery` hands components `{ status, data }` and NOT the response headers, so a wait the
 * server states only in `Retry-After` is invisible here; `retryAfterSeconds` in the body is the one
 * this app can read. Typed rather than reached through `any` so a rename stops compiling.
 */
export type MfaError = {
  status?: number;
  data?: {
    code?: string;
    error?: string;
    message?: string;
    retryAfterSeconds?: number;
    recoveryAvailable?: boolean;
  };
};

/** Reads an unknown RTK Query error as an MFA failure. Unknown shapes read as empty, never throw. */
export const asMfaError = (error: unknown): MfaError =>
  (error ?? {}) as MfaError;

/** Whether the provider has locked this account. Asked by all four screens, so it lives here. */
export const isUserLocked = (error: unknown): boolean =>
  asMfaError(error).data?.code === MFA_ERROR_CODE.userLocked;

export default mfaApi;

export const {
  useStartEnrollmentMutation,
  useVerifyEnrollmentMutation,
  useAckRecoveryCodesMutation,
  useVerifyTotpChallengeMutation,
  useVerifyRecoveryCodeMutation,
  useResetUserMfaMutation,
  useGetRolloutNoticeQuery,
  useLazyGetRolloutNoticeQuery,
} = mfaApi;
